// tabs/CourseTab.tsx
import { useCallback, useEffect, useState } from 'react';
import {
  Card, Group, Stack, Title, Text, Button, SimpleGrid,
  Divider, ActionIcon,
} from '@mantine/core';
import { IconPlus, IconRefresh, IconX } from '@tabler/icons-react';
import { Select } from '@/components';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useFacilitatorService, FacilitatorCourseView, useACCourseService, ACCourse } from '@actschurch/ccms-sdk';
import ConfirmActionModal from './ConfirmActionModal';

// ─── Props ────────────────────────────────────────────────────────────────────
interface CoursesTabProps {
  userId: string;
  active: boolean;
  onFacilitatorChange?: () => void;
}

// ─── CoursesTab ───────────────────────────────────────────────────────────────
// Displays courses assigned to a facilitator.
// Add via dropdown, remove via confirm modal.
export default function CoursesTab({ userId, active, onFacilitatorChange }: CoursesTabProps) {
  const facilitatorCourse = useFacilitatorService();
  const courseService     = useACCourseService();

  // Courses currently assigned to this facilitator
  const [assignedCourses, setAssignedCourses] = useState<FacilitatorCourseView[]>([]);
  // Full global course list for the add-dropdown
  const [allCourses, setAllCourses]           = useState<ACCourse[]>([]);
  const [loading, setLoading]                 = useState(false);

  // Dropdown selection state
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);

  // Confirm-remove modal state 
  const [confirmOpen, setConfirmOpen]       = useState(false);
  const [confirmTitle, setConfirmTitle]     = useState('Confirm Remove');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction]   = useState<(() => Promise<void>) | null>(null);

  // ── Fetch all global courses for the dropdown ──────────────────────────────
  useEffect(() => {
    courseService.getAll().then(setAllCourses).catch(console.error);
  }, [courseService]);

  // ── Fetch assigned courses for this facilitator ────────────────────────────
  const fetchAssignedCourses = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const result = await facilitatorCourse.getCourses(userId);
      setAssignedCourses(result);
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to load assigned courses.');
    } finally {
      setLoading(false);
    }
  }, [userId, facilitatorCourse]);

  // ── Only fetch assigned courses when this tab is active ───────────────────
  useEffect(() => {
    if (active) fetchAssignedCourses();
  }, [active, fetchAssignedCourses]);

  // ── Resolve the join-table row ID (NOT the courseID) ──────────────────────
  // This is the key fix for the 409 error — the remove endpoint expects the
  // facilitatorCourse row ID, not the courseID itself.
  const resolveRowId = (course: any): number =>
    course.facilitatorCourseID   // most specific — try first
    ?? course.facilitatorCourseId
    ?? course.id                 // generic fallback
    ?? course.courseID;          // last resort (causes 409 if used alone)

  // ── Resolve display name ───────────────────────────────────────────────────
  const resolveLabel = (course: any): string =>
    course.courseName ?? course.name ?? `Course ${course.courseID}`;

  // ── IDs already assigned — used to filter the dropdown ────────────────────
  const assignedIds = new Set(assignedCourses.map((c) => c.courseID));

  // ── Add course ────────────────────────────────────────────────────────────
  const handleAddCourse = async () => {
    if (!selectedCourseId || !userId) return;

    // Guard: prevent adding a duplicate
    if (assignedIds.has(Number(selectedCourseId))) {
      toast.error('Duplicate', 'This course is already assigned.');
      return;
    }

    setLoading(true);
    try {
      await facilitatorCourse.addCourse(
        userId,
        { courseID: Number(selectedCourseId) } as any
      );
      setSelectedCourseId(null);
      await fetchAssignedCourses();
      onFacilitatorChange?.();
      toast.success('Course Added', 'Course assigned to facilitator.');
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to add course.');
    } finally {
      setLoading(false);
    }
  };

  // ── Remove course — opens confirm modal with the row ID ───────────────────
  const handleRemoveCourse = (course: any) => {
    const rowId = resolveRowId(course);
    const name  = resolveLabel(course);

    setConfirmTitle('Remove Course');
    setConfirmMessage(`Remove this course${name ? ` (${name})` : ''} from the facilitator?`);

    // Store the action closure — called when the user confirms
    setConfirmAction(() => async () => {
      setLoading(true);
      try {
        // Pass the join-table row ID, not courseID
        await facilitatorCourse.removeCourse(userId, rowId as any);
        await fetchAssignedCourses();
        onFacilitatorChange?.();
        toast.warning('Course Removed', `"${name}" has been removed.`);
      } catch (e) {
        console.error(e);
        toast.error('Error', 'Failed to remove course.');
      } finally {
        setLoading(false);
      }
    });

    setConfirmOpen(true);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Shared confirm modal — reused across all tabs */}
      <ConfirmActionModal
        opened={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        onConfirm={async () => { if (confirmAction) await confirmAction(); }}
        onClose={() => { setConfirmOpen(false); setConfirmAction(null); }}
      />

      {/* Add course row */}
      <Group gap="sm" justify="space-between" mb="md">
        <Group gap="sm">
          <Select
            placeholder="Select a course to add"
            style={{ width: 300 }}
            data={allCourses
              .filter((c) => !assignedIds.has(c.courseID))
              .map((c) => ({
                value: String(c.courseID),
                label: resolveLabel(c),
              }))}
            value={selectedCourseId}
            onChange={setSelectedCourseId}
            searchable
            clearable
          />
          </Group>
          <Group gap="sm">
         
        <ActionIcon
          variant="subtle"
          size="lg"
          onClick={fetchAssignedCourses}
          loading={loading}
          aria-label="Refresh courses"
        >
          <IconRefresh size={16} />
        </ActionIcon>

         <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleAddCourse}
            disabled={!selectedCourseId || loading}
            loading={loading}
          >
            Add Course
          </Button>
        
        
        </Group>
      </Group>

      <Divider mb="md" />

      {/* Assigned courses grid */}
      {assignedCourses.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center">No courses assigned.</Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
          {assignedCourses.map((course: any) => (
            // Key on courseID since that's always present on the view model
            <Card key={course.courseID} withBorder radius="md" padding="lg">
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <div>
                  <Text fw={700}>{resolveLabel(course)}</Text>
                  {/* Registration count if available from the view model */}
                  {course.activeRegistrationsCount != null && (
                    <Text size="sm" c="dimmed">
                      {course.activeRegistrationsCount} registration
                      {course.activeRegistrationsCount !== 1 ? 's' : ''}
                    </Text>
                  )}
                </div>
                <ActionIcon
                  variant="light"
                  color="red"
                  onClick={() => handleRemoveCourse(course)}
                >
                  <IconX size={16} />
                </ActionIcon>
              </Group>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </>
  );
}